import { saveAs } from "file-saver";
import { useState } from "react";
import { SERVICES } from "../../../core/services/services";
import { CustomMessage } from "../../../shared/components/alert/custom-message";
import { CustomToast } from "../../../shared/components/alert/custom-toast";
import { CustomLoader } from "../../../shared/components/custom-loader/custom-loader";
import { CustomUpload } from "../../../shared/components/custom-upload/custom-upload";
import { Optional } from "../../../shared/components/optional/optional";
import { HELPER } from "../../../shared/helper/helper";
import { ConfigureSettlement } from "./configure-settlement";

function BulkUploadForm({ onSuccess, settlementInformation, goBack }) {
  const [loading, setLoading] = useState(false);
  const [bulkSuperAgents, setBulkSuperAgents] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  function getSettlementInformation() {
    const {
      vatAccount,
      vatAccountName,
      settlementType,
      concessionChargeType,
      chargeType,
      vatEnabled,
      participants,
    } = settlementInformation;
    return {
      participantsIds: participants.map((participant) => participant.id),
      vatEnabled,
      ...(vatAccount && { vatAccountNumber: vatAccount }),
      ...(vatAccountName && { vatAccountName: vatAccountName }),
      settlementType: settlementType?.code,
      chargeTypeCode: chargeType?.code,
      ...(concessionChargeType && {
        concessionChargeTypeCode: concessionChargeType?.code,
      }),
    };
  }

  function submit() {
    setLoading(true);
    let formData = new FormData();
    formData.append("file", bulkSuperAgents);
    const settlementInformation = getSettlementInformation();
    const settlementInfoProps = Object.keys(settlementInformation);
    settlementInfoProps.forEach((property) => {
      formData.append(property, settlementInformation[property]);
    });
    SERVICES.UPLOAD_BULK_MERCHANTS(formData)
      .then((response) => {
        const { result } = response;
        onSuccess(result?.uploadSummary, result?.errors);
        setLoading(false);
      })
      .catch((error) => {
        setErrorMessage(HELPER.PROCESS_ERROR(error));
        setLoading(false);
      });
  }

  function downloadTemplate(fileFormat) {
    saveAs(
      `${process.env.PUBLIC_URL}/sample-files/merchant-bulk-creation-sample.${fileFormat}`,
      `merchant-bulk-creation-sample.${fileFormat}`
    );
  }

  const submitButton = () => {
    if (!loading) {
      return (
        <button
          disabled={!bulkSuperAgents}
          onClick={submit}
          className="primary-button"
        >
          Submit
        </button>
      );
    } else {
      return (
        <div className="pull-up-element-2 p-mt-5">
          <CustomLoader loadingText="Submitting..." />
        </div>
      );
    }
  };

  return (
    <div>
      <CustomUpload
        accept=".csv,.xlsx"
        title={`Click to upload a .csv or .xlsx file for bulk merchant creation (Max 5MB)* `}
        fileType="spreadsheet"
        getUploadedFile={(file) => setBulkSuperAgents(file)}
        clearUploadedFile={() => setBulkSuperAgents(null)}
        noMaxSizeTip={true}
        maxFileSizeInMb="5"
      />
      <div className="p-text-left p-mt-4 p-d-flex p-justify-between">
        <button
          className="bare-button"
          onClick={() => downloadTemplate("xlsx")}
          style={{ fontSize: "0.9rem" }}
        >
          Download Excel Template
        </button>
        <button
          className="bare-button"
          onClick={() => downloadTemplate("csv")}
          style={{ fontSize: "0.9rem" }}
        >
          Download CSV Template
        </button>
      </div>
      <Optional show={errorMessage}>
        <div className="p-col-12">
          <CustomToast title="Error" description={errorMessage} type="error" />
        </div>
      </Optional>

      <div className="p-col-12 p-mb-5">
        <div className="p-mt-4">
          <div className="p-grid">
            <Optional show={!loading}>
              <div className="p-col-6 p-pl-0">
                <button onClick={goBack} className="secondary-button">
                  Back
                </button>
              </div>
            </Optional>
            <div className={`${loading ? "p-col-12" : "p-col-6"} p-pr-0`}>
              {submitButton()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BulkSuperAgentsCreationForm(props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [settlementInformation, setSettlementInformation] = useState(null);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);

  function currentFormView() {
    switch (currentIndex) {
      case 0:
        return (
          <ConfigureSettlement
            isBulkUpload={true}
            onSaveSettlementInformation={(data) => {
              setSettlementInformation(data);
              setCurrentIndex(1);
            }}
            // onNext={() => setCurrentIndex(1)}
          />
        );
      case 1:
        return (
          <BulkUploadForm
            settlementInformation={settlementInformation}
            onSuccess={(message, errors) => {
              setUploadSummary(message);
              setUploadErrors(errors);
              setCurrentIndex(2);
            }}
            closeModal={props.closeModal}
            goBack={() => setCurrentIndex(0)}
          />
        );
      case 2:
        return (
          <div>
            <div>
              <CustomMessage messageType="success" />
            </div>
            <div>
              <p
                className="success-message-text p-text-left"
                style={{ fontSize: "0.9rem" }}
              >
                {uploadSummary}
              </p>
              <Optional show={uploadErrors.length}>
                <p className="p-mb-0 p-text-left text-small">Errors:</p>
                {uploadErrors.map((error, index) => (
                  <p
                    key={index}
                    className="p-mt-0 p-text-left"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {error}
                  </p>
                ))}
              </Optional>
            </div>
            <div className="success-message-btn-container p-text-center">
              <p
                onClick={() => {
                  props.closeModal(true);
                }}
                className="close-modal add-cursor"
              >
                Close
              </p>
            </div>
          </div>
        );
      default:
        break;
    }
  }

  return (
    <>
      {currentIndex !== 2 && (
        <div>
          <div className="custom-modal-title p-text-left p-mb-3 p-mt-0">
            Bulk Super Agent Creation
          </div>
          <div className="custom-dialog-subtitle-container p-mb-5">
            <p className="custom-dialog-subtitle">Fill the form below</p>
            <p className="custom-dialog-subtitle">
              <span className="p-text-error">*</span> Required Fields
            </p>
          </div>
        </div>
      )}
      {currentFormView()}
    </>
  );
}
